'use client';

import { c as _c } from "react/compiler-runtime";
import { useArchivedWallets, useWallets } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { ArrowRight01Icon } from '@/components/ui/icon/glyphs';
import { WalletBalanceDisplay } from '@/components/wallet/wallet-balance-display';
import { TOAST_IDS } from '@/config/toast-ids';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import type { Wallet } from '@/types/auth';
function formatArchivedDate(archivedAt: string, locale: string): string {
  const date = new Date(archivedAt);
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}
function getTypeBadgeKey(type: Wallet['type']): string {
  switch (type) {
    case 'embedded':
      return 'type_embedded';
    case 'imported':
      return 'type_imported';
    case 'external':
      return 'type_external';
  }
}
interface ArchivedWalletRowProps {
  wallet: Wallet;
  onRestore: (walletId: string) => void;
  isRecovering: boolean;
  tTable: (key: string) => string;
  tRestore: (key: string) => string;
  tArchivedOn: (params: {
    date: string;
  }) => string;
}
function ArchivedWalletRow(t0) {
  const $ = _c(31);
  const {
    wallet,
    onRestore,
    isRecovering,
    tTable,
    tRestore,
    tArchivedOn
  } = t0;
  let t1;
  if ($[0] !== wallet.label) {
    t1 = <span className="text-body font-medium text-foreground-primary">{wallet.label}</span>;
    $[0] = wallet.label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== tTable || $[3] !== wallet.type) {
    t2 = tTable(getTypeBadgeKey(wallet.type) as Parameters<typeof tTable>[0]);
    $[2] = tTable;
    $[3] = wallet.type;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <span className="rounded-full bg-background-tertiary px-2 py-0.5 text-caption">{t2}</span>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== tArchivedOn || $[8] !== wallet.archivedAt) {
    t4 = wallet.archivedAt !== null && <span className="ml-2">{tArchivedOn({
        date: formatArchivedDate(wallet.archivedAt, "en-US")
      })}</span>;
    $[7] = tArchivedOn;
    $[8] = wallet.archivedAt;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <span className="text-caption text-foreground-secondary">{t3}{t4}</span>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t1 || $[14] !== t5) {
    t6 = <div className="flex flex-1 flex-col gap-0.5">{t1}{t5}</div>;
    $[13] = t1;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== onRestore || $[17] !== wallet.id) {
    t7 = () => {
      onRestore(wallet.id);
    };
    $[16] = onRestore;
    $[17] = wallet.id;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== tRestore) {
    t8 = tRestore("button");
    $[19] = tRestore;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== isRecovering || $[22] !== t7 || $[23] !== t8) {
    t9 = <AnimatedButton variant="secondary" size="sm" disabled={isRecovering} onClick={t7}>{t8}</AnimatedButton>;
    $[21] = isRecovering;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== wallet.address) {
    t10 = <div className="min-w-16"><WalletBalanceDisplay address={wallet.address} variant="combined" forceIncludeHoldings={true} align="right" primaryClassName="text-body font-medium text-foreground-primary" /></div>;
    $[25] = wallet.address;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t6 || $[29] !== t9) {
    t11 = <div className="flex items-center gap-4 rounded-card border px-4 py-3">{t6}{t9}{t10}</div>;
    $[27] = t10;
    $[28] = t6;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  return t11;
}
export function ArchivedWalletsSection() {
  const $ = _c(27);
  const t = useTranslations("wallet.archived_section");
  const tTable = useTranslations("wallet.table");
  const tRestore = useTranslations("wallet.restore");
  const {
    archivedWallets,
    isLoading
  } = useArchivedWallets();
  const {
    recoverWallet,
    isRecovering
  } = useWallets();
  const {
    enabled: multiWalletsEnabled,
    canStartOperation
  } = useMultiWalletFeature();
  let t0;
  if ($[0] !== canStartOperation || $[1] !== recoverWallet || $[2] !== tRestore) {
    t0 = async function handleRestore(walletId) {
      if (!canStartOperation("restore", "wallet_management")) {
        return;
      }
      ;
      try {
        await recoverWallet(walletId);
        showToast({
          id: TOAST_IDS.WALLET_RESTORED,
          message: tRestore("success"),
          variant: "success"
        });
      } catch (t1) {
        const err = t1;
        const message = err instanceof Error ? err.message : String(err);
        logger.error("Wallet restore failed", {
          error: message.slice(0, 200)
        });
        showToast({
          id: TOAST_IDS.WALLET_RESTORED,
          message,
          variant: "error"
        });
      }
    };
    $[0] = canStartOperation;
    $[1] = recoverWallet;
    $[2] = tRestore;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const handleRestore = t0;
  if (!multiWalletsEnabled || isLoading) {
    return null;
  }
  if (archivedWallets.length === 0) {
    return null;
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Icon icon={ArrowRight01Icon} className="size-4 shrink-0 transition-transform group-open:rotate-90" aria-hidden={true} />;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t) {
    t2 = t("title");
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <summary className="flex cursor-pointer list-none items-center justify-start gap-2 text-body font-medium text-foreground-secondary [&::-webkit-details-marker]:hidden">{t1}<span>{t2}</span></summary>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== archivedWallets || $[10] !== handleRestore || $[11] !== isRecovering || $[12] !== t || $[13] !== tRestore || $[14] !== tTable) {
    let t5;
    if ($[16] !== handleRestore || $[17] !== isRecovering || $[18] !== t || $[19] !== tRestore || $[20] !== tTable) {
      t5 = wallet => <ArchivedWalletRow key={wallet.id} wallet={wallet} onRestore={id => {
        handleRestore(id);
      }} isRecovering={isRecovering} tTable={tTable as (key: string) => string} tRestore={tRestore as (key: string) => string} tArchivedOn={params => t("archived_on", params)} />;
      $[16] = handleRestore;
      $[17] = isRecovering;
      $[18] = t;
      $[19] = tRestore;
      $[20] = tTable;
      $[21] = t5;
    } else {
      t5 = $[21];
    }
    t4 = archivedWallets.map(t5);
    $[9] = archivedWallets;
    $[10] = handleRestore;
    $[11] = isRecovering;
    $[12] = t;
    $[13] = tRestore;
    $[14] = tTable;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[22] !== t4) {
    t5 = <div className="mt-3"><div className="flex flex-col gap-2">{t4}</div></div>;
    $[22] = t4;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== t3 || $[25] !== t5) {
    t6 = <details className="group w-full">{t3}{t5}</details>;
    $[24] = t3;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  return t6;
}